import React, { ChangeEvent, PureComponent, SyntheticEvent } from 'react';
import styled from 'styled-components';
import { Colors, Button, Label, Tooltip, Switch, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { FaRegEdit } from 'react-icons/fa';

// Types
import { Item } from 'components/BoxItem';

// Components
import TextInput from 'components/TextInput';
import Restricted from 'restrictions/roles/components/Restricted';
import { BlueTypography } from 'components/SharedStyledElements';

const OauthItemContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  cursor: auto;
  overflow: visible;
  background: ${Colors.white};
`;

const HeaderContainer = styled.div`
  width: 100%;
  height: 2rem;
  display: flex;
  justify-content: space-between;
`;

const MiddleContainer = styled.div`
  width: 100%;
`;

const IconWithMargin = styled(FaRegEdit)`
  margin-left: 0.5rem;
`;

const GrayText = styled.span`
  color: ${Colors.fontGray};
`;

const WrappedSpan = styled.span`
  line-height: 1.5;
  word-break: break-all;
  margin-left: 1rem;
`;

const SwitchContainer = styled.div`
  width: 100%;
  border-top: 1px solid ${Colors.gray};
  padding-top: 1rem;
`;

const StyledTooltip = styled(Tooltip)`
  width: 100%;
`;

const StyledLabel = styled(Label)`
  display: flex;
  justify-content: space-between;
`;

interface OwnProps {
  boxItem: Item;
  onNameChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onTitleChange: (app: Item) => void;
  onClickGenerateNew: (id: string) => void;
  toggleSSO: (params: { checked: boolean; id: string }) => any;
  filled: boolean;
  checked: boolean;
  isReadOnly?: boolean;
}

interface State {
  currentName: string;
  editMode: boolean;
}

class OauthItem extends PureComponent<OwnProps, State> {
  static defaultProps = { isReadOnly: false };

  state: State = {
    currentName: '',
    editMode: false,
  };

  onEditName = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const {
      target: { value: currentName },
    } = e;

    const { onNameChange } = this.props;

    this.setState({ currentName });

    onNameChange(e);
  };

  onBlur = async (e: SyntheticEvent) => {
    e.preventDefault();

    const { boxItem, onTitleChange } = this.props;
    const { currentName } = this.state;

    if (currentName && currentName !== boxItem.name) {
      await onTitleChange(boxItem);
    }

    this.exitEditMode();
  };

  generateNew = (e: SyntheticEvent) => {
    e.preventDefault();

    const {
      boxItem: { id },
      onClickGenerateNew,
    } = this.props;

    onClickGenerateNew(id.toString());
  };

  toggleSSOswitch = (checked: boolean) => {
    const {
      toggleSSO,
      boxItem: { id },
    } = this.props;

    toggleSSO({ checked, id: id.toString() });
  };

  enterEditMode = (isReadOnly: boolean) => () => {
    if (isReadOnly) {
      return;
    }

    this.setState({ editMode: true });
  };

  exitEditMode = () => {
    this.setState({ editMode: false });
  };

  validateName = (name?: string) => !!name;

  render() {
    const { boxItem, filled, checked, isReadOnly = false } = this.props;
    const { editMode } = this.state;

    const tooltip = isReadOnly ? 'App name' : 'Click to edit...';

    return (
      <OauthItemContainer>
        <HeaderContainer>
          {editMode ? (
            <TextInput
              tag={`text_input_${boxItem.id}`}
              autoFocus
              placeholder="name"
              defaultValue={boxItem.name}
              onChange={this.onEditName}
              onBlur={this.onBlur}
              onEnterKey={this.onBlur}
              onEscapeKey={this.exitEditMode}
              validationOnEnter={this.validateName}
            />
          ) : (
            <StyledTooltip placement="top" content={tooltip}>
              <StyledLabel onClick={this.enterEditMode(isReadOnly)}>
                <BlueTypography variant="h5">{boxItem.name}</BlueTypography>
                {!isReadOnly && (
                  <AnalyticsComponent>
                    {({ pages, tracker, merchantId, ip }) => (
                      <IconWithMargin
                        onClick={() =>
                          tracker.track({
                            event: 'click',
                            type: 'icon',
                            pages,
                            tag: `icon_edit_${boxItem.id}`,
                            merchantId,
                            ip,
                          })
                        }
                      />
                    )}
                  </AnalyticsComponent>
                )}
              </StyledLabel>
            </StyledTooltip>
          )}
        </HeaderContainer>
        <MiddleContainer>
          <div>
            <GrayText>Key:</GrayText>
            <WrappedSpan>{boxItem.id}</WrappedSpan>
          </div>
          <Restricted isRestricted={isReadOnly}>
            <div>
              <GrayText>App secret key:</GrayText>
              <Button
                tag={`button_generate_new_${boxItem.id}`}
                buttonModifiers={['buttonLink']}
                onClick={this.generateNew}
              >
                Generate new...
              </Button>
            </div>
          </Restricted>
        </MiddleContainer>
        <SwitchContainer>
          {filled && (
            <Switch
              tag={`toggle_enable_sso_${boxItem.id}`}
              id={`sso-switch-app-${boxItem.id}`}
              label={checked ? 'SSO enabled' : 'Enable SSO'}
              checked={checked}
              onChange={this.toggleSSOswitch}
              disabled={isReadOnly}
            />
          )}
        </SwitchContainer>
      </OauthItemContainer>
    );
  }
}

export default OauthItem;
